import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, Copy, Check } from 'lucide-react';
import { DashboardLayout } from '../components/dashboard/DashboardLayout';
import { BasicsForm } from '../components/signup/BasicsForm';
import { ProfilePreview } from '../components/signup/ProfilePreview';
import { BrandOnboarding } from '../components/dashboard/BrandOnboarding';
import { OrganizerOnboarding } from '../components/dashboard/OrganizerOnboarding';
import { registerUser } from '../lib/airtable';
interface SignupFlowProps {
  role: 'brand' | 'organizer';
  onComplete: (data: any) => void;
  onBack: () => void;
}
type Phase = 'basics' | 'preview' | 'profile' | 'complete';
export function SignupFlow({
  role,
  onComplete,
  onBack
}: SignupFlowProps) {
  const [phase, setPhase] = useState<Phase>('basics');
  const [basicsData, setBasicsData] = useState<any>(null);
  const [accessCode, setAccessCode] = useState<string>('');
  const [copied, setCopied] = useState(false);
  const [fullProfile, setFullProfile] = useState<any>(null);
  const handleBasicsNext = (data: any) => {
    setBasicsData(data);
    setPhase('preview');
  };
  const handlePreviewNext = () => {
    setPhase('profile');
  };
  const handleProfileComplete = async (data: any) => {
    // Merge basics with profile data
    const profile = {
      ...basicsData,
      ...data
    };
    setFullProfile(profile);
    // Register user and generate access code
    const result = await registerUser({
      name: basicsData.name,
      email: basicsData.email,
      type: role === 'brand' ? 'Brand' : 'Organizer',
      industry: basicsData.type,
      profileData: profile
    });
    if (result.accessCode) {
      setAccessCode(result.accessCode);
      // Store in localStorage for auto-login
      localStorage.setItem('sponsrai_access', result.accessCode);
      localStorage.setItem('sponsrai_user', JSON.stringify({
        name: basicsData.name,
        email: basicsData.email,
        type: role === 'brand' ? 'Brand' : 'Organizer'
      }));
    }
    setPhase('complete');
  };
  const copyCode = () => {
    navigator.clipboard.writeText(accessCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const handleContinue = () => {
    onComplete({
      ...fullProfile,
      accessCode
    });
  };
  const accentColor = role === 'brand' ? 'cyan' : 'purple';
  const buttonColor = role === 'brand' ? 'bg-cyan-500 hover:bg-cyan-400 text-slate-900' : 'bg-purple-600 hover:bg-purple-500 text-white';
  return <div className="min-h-screen bg-slate-950 text-slate-50" data-id="element-994">
      <DashboardLayout role={role} onBack={onBack} data-id="element-995">
        <AnimatePresence mode="wait" data-id="element-996">
          {phase === 'basics' && <motion.div key="basics" initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} className="py-12" data-id="element-997">
              <BasicsForm role={role} onNext={handleBasicsNext} data-id="element-998" />
            </motion.div>}

          {phase === 'preview' && <motion.div key="preview" initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} className="py-12" data-id="element-999">
              <ProfilePreview role={role} basics={basicsData} onNext={handlePreviewNext} data-id="element-1000" />
            </motion.div>}

          {phase === 'profile' && <motion.div key="profile" initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} className="py-8" data-id="element-1001">
              <div className="max-w-3xl mx-auto mb-8 text-center" data-id="element-1002">
                <h2 className="text-3xl font-bold text-white mb-2" data-id="element-1003">
                  Build Your Profile
                </h2>
                <p className="text-slate-400" data-id="element-1004">
                  Let's fill in the details to find your perfect match.
                </p>
              </div>

              {role === 'brand' ? <BrandOnboarding onComplete={handleProfileComplete} data-id="element-1005" /> : <OrganizerOnboarding onComplete={handleProfileComplete} data-id="element-1006" />}
            </motion.div>}

          {phase === 'complete' && <motion.div key="complete" initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="py-20 max-w-xl mx-auto text-center" data-id="element-1007">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 200,
            damping: 15,
            delay: 0.2
          }} className={`w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-8 ${role === 'brand' ? 'bg-cyan-500/20' : 'bg-purple-500/20'}`} data-id="element-1008">
                <CheckCircle2 size={48} className={role === 'brand' ? 'text-cyan-500' : 'text-purple-500'} data-id="element-1009" />
              </motion.div>

              <h1 className="text-4xl font-bold text-white mb-4" data-id="element-1010">
                You're all set! 🎉
              </h1>
              <p className="text-xl text-slate-400 mb-8" data-id="element-1011">
                Your profile is ready. Here's your personal login code:
              </p>

              <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6 mb-8" data-id="element-1012">
                <p className="text-sm text-slate-500 mb-3" data-id="element-1013">Your Access Code</p>
                <div className="flex items-center justify-center gap-3" data-id="element-1014">
                  <code className={`text-2xl font-mono font-bold ${role === 'brand' ? 'text-cyan-400' : 'text-purple-400'}`} data-id="element-1015">
                    {accessCode}
                  </code>
                  <button onClick={copyCode} className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors" data-id="element-1016">
                    {copied ? <Check size={20} data-id="element-1017" /> : <Copy size={20} data-id="element-1018" />}
                  </button>
                </div>
                <p className="text-xs text-slate-500 mt-3" data-id="element-1019">
                  Save this code! You'll need it to log back in.
                </p>
              </div>

              <button onClick={handleContinue} className={`px-10 py-4 rounded-full font-bold text-lg transition-all ${buttonColor}`} data-id="element-1020">
                Go to Dashboard
              </button>
            </motion.div>}
        </AnimatePresence>
      </DashboardLayout>
    </div>;
}